import { useState } from 'react';
import {
  GlassCard,
  SettingsPageHeader,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { ToggleSwitch } from './ToggleSwitch';

type PushPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

function getPushPermissionState(): PushPermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission as PushPermissionState;
}

export function NotificationsScene() {
  const [enabled, setEnabled] = useState(false);
  const [permission] = useState<PushPermissionState>(getPushPermissionState);

  const denied = permission === 'denied';
  const unsupported = permission === 'unsupported';

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="NotificationsScene">
        <div className="contents">
          <SurfacePageBody className="space-y-8" as="div">
          <SettingsPageHeader
            title="Notifications"
            description="Browser push for background agent completions."
          />

          <Commentable name="PushSection">
            <section>
              <h2 className="text-sm font-medium uppercase tracking-wide text-muted">
                Push notifications
              </h2>
              <Commentable name="PushToggle">
              <GlassCard className="mt-3 overflow-hidden">
                <div className="flex items-start justify-between gap-6 px-5 py-4">
                  <div className="min-w-0 flex-1">
                    <label
                      htmlFor="push-notifications"
                      className="block font-medium text-zinc-900 dark:text-zinc-100"
                    >
                      Push notifications
                    </label>
                    <p className="mt-1 text-sm text-muted">
                      Get notified when a delegated research run completes while you are
                      away from the tab.
                    </p>
                    {denied ? (
                      <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">
                        Notifications are blocked in browser settings.
                      </p>
                    ) : null}
                    {unsupported ? (
                      <p className="mt-2 text-sm text-muted">
                        Push notifications are not supported in this browser.
                      </p>
                    ) : null}
                    {permission === 'default' && !denied && !unsupported ? (
                      <p className="mt-2 text-sm text-muted">
                        Permission not yet requested — toggling on will prompt your browser.
                      </p>
                    ) : null}
                    {permission === 'granted' ? (
                      <p className="mt-2 text-sm text-muted">
                        Browser permission granted.
                      </p>
                    ) : null}
                  </div>
                  <ToggleSwitch
                    id="push-notifications"
                    checked={enabled}
                    onCheckedChange={setEnabled}
                    disabled={denied || unsupported}
                  />
                </div>
              </GlassCard>
              </Commentable>
            </section>
          </Commentable>
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}
